import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  createColorVehicle,
  createColorVehicleCompany,
  createModelVehicle,
  createModelVehicleCompany,
  createModelYearVehicle,
  createModelYearVehicleCompany,
  createStorageLocation,
  createStorageLocationCompany,
  getColorsVehicle,
  getColorsVehicleCompany,
  getModelsVehicle,
  getModelsVehiclesCompany,
  getModelYear,
  getModelYearVehiclesCompany,
  getStorageLocation,
  getStorageLocationCompany,
  getStorageType,
} from "../../../api/vehicleApi";
import {
  ColorData,
  Models,
  StorageLocation,
  TypeStorageLocation,
  YearModel,
} from "../../../types/vehicleData";
import { useLocation } from "react-router-dom";

interface CreateModalProps {
  open: boolean;
  fieldName: string;
  onClose: () => void;
  setCorOptions: React.Dispatch<
    React.SetStateAction<{ id: number; descricao_cor: string }[]>
  >;
  setModelOptions: React.Dispatch<
    React.SetStateAction<{ id: number; descricao_modelo: string }[]>
  >;
  setYearOptions: React.Dispatch<
    React.SetStateAction<{ id: number; descricao_ano_modelo: string }[]>
  >;
  setLocalStorageOptions: React.Dispatch<
    React.SetStateAction<{ id: number; descricao_local: string }[]>
  >;
}

export const CreateModal: React.FC<CreateModalProps> = ({
  open,
  fieldName,
  onClose,
  setCorOptions,
  setModelOptions,
  setYearOptions,
  setLocalStorageOptions,
}) => {
  const [newItem, setNewItem] = useState("");
  const [selectedLocationType, setSelectedLocationType] = useState<number>(0);
  const [storageTypes, setStorageTypes] = useState<TypeStorageLocation[]>([]);
  const location = useLocation();

  const getQueryParam = (param: string) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(param);
  };

  const companyId = getQueryParam("idEmpresa");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewItem(event.target.value);
  };

  useEffect(() => {
    const fetchStorageTypes = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const storageTypesData: TypeStorageLocation[] = await getStorageType(
            token
          );
          setStorageTypes(storageTypesData);
        }
      } catch (error) {
        console.error("Erro ao buscar tipos de local de armazenagem:", error);
        toast.error("Erro ao buscar tipos de local de armazenagem.");
      }
    };

    fetchStorageTypes();
  }, []);

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("userData");

      if (!token || !userData) {
        toast.error("Autenticação necessária");
        return;
      }

      const parsedUserData = JSON.parse(userData);
      const isAdmin = parsedUserData.admin && companyId;

      if (fieldName === "Cor") {
        if (isAdmin) {
          await createColorVehicleCompany(
            token,
            { descricao_cor: newItem, id_empresa: parseInt(companyId) },
            companyId
          );
          const colorsData = await getColorsVehicleCompany(token, companyId);
          const colors = colorsData.map((color: ColorData) => ({
            id: color.id,
            descricao_cor: color.descricao_cor,
          }));
          setCorOptions(colors);
        } else {
          await createColorVehicle(token, { descricao_cor: newItem });
          const colorsData = await getColorsVehicle(token);
          const colors = colorsData.map((color: ColorData) => ({
            id: color.id,
            descricao_cor: color.descricao_cor,
          }));
          setCorOptions(colors);
        }
      } else if (fieldName === "Modelo") {
        if (isAdmin) {
          await createModelVehicleCompany(token, {
            descricao_modelo: newItem,
            id_empresa: parseInt(companyId),
          });
          const modelsData = await getModelsVehiclesCompany(token, companyId);
          const models = modelsData.map((model: Models) => ({
            id: model.id,
            descricao_modelo: model.descricao_modelo,
          }));
          setModelOptions(models);
        } else {
          await createModelVehicle(token, { descricao_modelo: newItem });
          const modelsData = await getModelsVehicle(token);
          const models = modelsData.map((model: Models) => ({
            id: model.id,
            descricao_modelo: model.descricao_modelo,
          }));
          setModelOptions(models);
        }
      } else if (fieldName === "Ano / Modelo") {
        if (isAdmin) {
          await createModelYearVehicleCompany(
            token,
            { descricao_ano_modelo: newItem, id_empresa: parseInt(companyId) },
            companyId
          );
          const yearModels = await getModelYearVehiclesCompany(
            token,
            companyId
          );
          const years = yearModels.map((year: YearModel) => ({
            id: year.id,
            descricao_ano_modelo: year.descricao_ano_modelo,
          }));
          setYearOptions(years);
        } else {
          await createModelYearVehicle(token, {
            descricao_ano_modelo: newItem,
          });
          const yearModels = await getModelYear(token);
          const years = yearModels.map((year: YearModel) => ({
            id: year.id,
            descricao_ano_modelo: year.descricao_ano_modelo,
          }));
          setYearOptions(years);
        }
      } else if (fieldName === "Local Armazenagem") {
        if (isAdmin) {
          await createStorageLocationCompany(
            token,
            {
              descricao_local: newItem,
              id_tipo_local: selectedLocationType,
              id_empresa: parseInt(companyId),
            },
            companyId
          );
          const storageLocationModels = await getStorageLocationCompany(
            token,
            companyId
          );
          const storageLocation = storageLocationModels.map(
            (location: StorageLocation) => ({
              id: location.id,
              descricao_local: location.descricao_local,
            })
          );
          setLocalStorageOptions(storageLocation);
        } else {
          await createStorageLocation(token, {
            descricao_local: newItem,
            id_tipo_local: selectedLocationType,
          });
          const storageLocationModels = await getStorageLocation(token);
          const storageLocation = storageLocationModels.map(
            (location: StorageLocation) => ({
              id: location.id,
              descricao_local: location.descricao_local,
            })
          );
          setLocalStorageOptions(storageLocation);
        }
      }

      onClose();
      setNewItem("");
      toast.success(`${fieldName} criado com sucesso!`);
    } catch (error) {
      console.error(`Erro ao criar novo ${fieldName}:`, error);
      toast.error(`Erro ao criar novo ${fieldName}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Criar {fieldName}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="newItem"
          label={`Novo ${fieldName}`}
          type="text"
          fullWidth
          value={newItem}
          onChange={handleChange}
        />
        {fieldName === "Local Armazenagem" && (
          <TextField
            select
            fullWidth
            value={selectedLocationType}
            onChange={(e) => setSelectedLocationType(Number(e.target.value))}
            label="Tipo de Local de Armazenagem"
            sx={{ marginTop: 4 }}
          >
            {storageTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.descriacao_tipo_local}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleCreate}>Criar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateModal;
